import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  AfterViewInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { SessionStorageService } from '@mkp/shared/util-session-storage';
import { HomePageActions } from '@mkp/core/feature-home/actions';
import { DemoProductsComponent, ReplacePicturePipe } from '@mkp/core/ui';
import {
  ActionState,
  Button,
  ButtonColor,
  ButtonComponent,
  ButtonSize,
  PromptLinkComponent,
  TabConfig,
  TabItemComponent,
  TabSize,
  TabsComponent,
} from '@mkp/shared/ui-library';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { BreakpointObserverService } from '@mkp/shared/util-device';
import { DataTestDirective } from '@mkp/tracking/feature-tracking';
import { ZendeskService } from '@mkp/shared/util-zendesk';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@mkp/shared/ui-material';
import { selectInterfaceLanguage } from '@mkp/core/state';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { AssetComponent } from '@mkp/shared/ui-asset';
import { AccountManagerComponent } from '@mkp/shared/ui-account-manager';

@Component({
  selector: 'mkp-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    MaterialModule,
    InlineSVGModule,
    TranslateModule,
    TabsComponent,
    TabItemComponent,
    DemoProductsComponent,
    ReplacePicturePipe,
    DataTestDirective,
    NgOptimizedImage,
    AssetComponent,
    AccountManagerComponent,
    PromptLinkComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HomeComponent implements OnInit, AfterViewInit {
  private readonly cd = inject(ChangeDetectorRef);
  private readonly notificationsService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly store = inject(Store);
  public readonly breakpointObserverService = inject(BreakpointObserverService);
  public readonly destroy = inject(DestroyRef);
  public readonly zendeskService = inject(ZendeskService);
  private readonly activatedRoute = inject(ActivatedRoute);

  /**
   * @deprecated
   */
  public ladda$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isMobile$ = this.breakpointObserverService.isMobile$;
  readonly language$ = this.store.select(selectInterfaceLanguage);
  readonly primaryRegisterButtonConfig: Button = {
    color: ButtonColor.Primary,
    size: ButtonSize.Medium,
    fullWidth: true,
  };
  readonly tabsConfig: TabConfig = {
    size: TabSize.Large,
  };
  readonly tabs = [
    {
      title: 'HOME.TABS.JOB_ADS',
      index: 0,
      dataTest: 'job-ads',
      imageSrc: 'job-ads',
    },
    {
      title: 'HOME.TABS.CANDIDATE_PROFILE',
      index: 1,
      dataTest: 'candidate-profile',
      imageSrc: 'candidate',
    },
    {
      title: 'HOME.TABS.COMPANY_PROFILE',
      index: 2,
      dataTest: 'company-profile',
      imageSrc: 'company',
    },
  ];

  readonly homepageImageUrl = 'assets/images/homepage/redesign';
  contactUsButtonConfig: Button = {
    size: ButtonSize.Medium,
  };

  vm$ = combineLatest({
    isMobile: this.isMobile$,
    language: this.language$,
  });

  ngOnInit() {
    this.store.dispatch(HomePageActions.enter());

    this.breakpointObserverService.isMobile$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((isMobile) => {
        const btnColor = isMobile ? ButtonColor.Secondary : ButtonColor.Primary;

        this.contactUsButtonConfig = {
          ...this.contactUsButtonConfig,
          color: btnColor,
        };

        this.cd.markForCheck();
      });

    // JMP-4357 - auth0 refreshes the page after a logout, look into session to see if logout was forced because of an error
    if (this.sessionStorageService.get('forcedLogout')) {
      this.handleForcedLogout();
    }

    // CDH-1325 - handle post-logout redirect for redeem invite URLs
    const redeemInviteUrl = this.sessionStorageService.get('redeemInviteUrl');
    if (redeemInviteUrl) {
      this.handleRedeemInviteUrl(redeemInviteUrl);
    }
  }

  ngAfterViewInit() {
    const currentFragment = this.activatedRoute.snapshot.fragment;
    if (currentFragment === 'products') {
      setTimeout(() => {
        this.scrollToProducts();
      }, 100);
    }
  }

  openZendesk() {
    this.zendeskService.openWidget();
  }

  private handleForcedLogout() {
    this.sessionStorageService.remove('forcedLogout');
    this.router
      .navigateByUrl(AppConfig.routes.error500)
      .then(() =>
        this.notificationsService.show('REQUEST_TIMEOUT_MESSAGE', { state: ActionState.Error })
      );
  }

  private handleRedeemInviteUrl(redeemInviteUrl: string) {
    this.sessionStorageService.remove('redeemInviteUrl');
    this.router.navigateByUrl(redeemInviteUrl);
  }

  private scrollToProducts() {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
